// Curated skincare product database
// No marketing copy - ingredient-science focused descriptions only

export type SkinType = 'oily' | 'dry' | 'combination' | 'sensitive' | 'normal';
export type AcneType = 'mild' | 'moderate' | 'severe' | 'hormonal' | 'inflammatory' | 'post-acne';
export type PriceRange = '$' | '$$' | '$$$';

// Helper constants
const ALL_SKIN_TYPES: SkinType[] = ['oily', 'dry', 'combination', 'sensitive', 'normal'];
const ALL_ACNE_TYPES: AcneType[] = ['mild', 'moderate', 'severe', 'hormonal', 'inflammatory', 'post-acne'];
export type ProductTag = 
  | 'k-beauty' 
  | 'influencer' 
  | 'natural' 
  | 'hydrating' 
  | 'barrier-repair' 
  | 'anti-inflammatory' 
  | 'brightening' 
  | 'exfoliating' 
  | 'acne-control' 
  | 'budget' 
  | 'student-pick'
  | 'gentle'
  | 'calming';

export interface Product {
  id: string;
  brand: string;
  productCategory: string;
  recommendedFor: {
    skinTypes: SkinType[];
    acneTypes: AcneType[];
    concerns: string[];
  };
  keyIngredients: string[];
  ingredientBenefits: string; // Science-based explanation
  priceRange: PriceRange;
  price?: string; // Actual price (e.g., "$12.99", "$24.50")
  tags: ProductTag[];
  // Placeholder fields for future affiliate integration
  brandWebsite?: string;
  affiliateLinks?: {
    platform: string;
    url: string;
  }[];
}

export const products: Product[] = [
  // K-BEAUTY ESSENTIALS
  {
    id: 'cosrx-snail-essence',
    brand: 'Cosrx',
    productCategory: 'Hydration + Repair Essence',
    recommendedFor: {
      skinTypes: ['oily', 'combination', 'dry'],
      acneTypes: ['mild', 'post-acne', 'inflammatory'],
      concerns: ['damaged barrier', 'hydration', 'healing']
    },
    keyIngredients: ['snail mucin (96%)', 'panthenol'],
    ingredientBenefits: 'Snail secretion filtrate provides glycoproteins and hyaluronic acid for moisture retention and barrier repair. Supports wound healing and reduces inflammation.',
    priceRange: '$',
    price: '$14.99',
    tags: ['k-beauty', 'hydrating', 'barrier-repair', 'budget', 'calming'],
    brandWebsite: '',
    affiliateLinks: []
  },
  {
    id: 'beauty-of-joseon-relief-sun',
    brand: 'Beauty of Joseon',
    productCategory: 'Sunscreen (SPF 50+)',
    recommendedFor: {
      skinTypes: ['oily', 'dry', 'combination', 'sensitive', 'normal'],
      acneTypes: ['mild', 'moderate', 'severe', 'hormonal', 'inflammatory', 'post-acne'],
      concerns: ['sun protection', 'non-comedogenic']
    },
    keyIngredients: ['zinc oxide', 'rice extract', 'grain ferment'],
    ingredientBenefits: 'Physical UV protection with zinc oxide. Rice extract provides antioxidants. Lightweight texture suitable for acne-prone skin without clogging pores.',
    priceRange: '$',
    price: '$12.99',
    tags: ['k-beauty', 'budget', 'gentle', 'student-pick'],
    brandWebsite: '',
    affiliateLinks: []
  },
  {
    id: 'beauty-of-joseon-glow-serum',
    brand: 'Beauty of Joseon',
    productCategory: 'Calming + Brightening Serum',
    recommendedFor: {
      skinTypes: ['oily', 'combination', 'sensitive'],
      acneTypes: ['post-acne', 'mild', 'hormonal'],
      concerns: ['dark spots', 'inflammation', 'redness']
    },
    keyIngredients: ['niacinamide (2%)', 'arbutin', 'rice bran water'],
    ingredientBenefits: 'Niacinamide regulates sebum and reduces inflammation. Arbutin inhibits melanin production for hyperpigmentation. Rice water provides antioxidants.',
    priceRange: '$',
    price: '$13.50',
    tags: ['k-beauty', 'brightening', 'anti-inflammatory', 'budget'],
    brandWebsite: '',
    affiliateLinks: []
  },
  {
    id: 'cosrx-bha-liquid',
    brand: 'Cosrx',
    productCategory: 'Gentle Chemical Exfoliant',
    recommendedFor: {
      skinTypes: ['oily', 'combination'],
      acneTypes: ['mild', 'moderate', 'hormonal'],
      concerns: ['clogged pores', 'blackheads', 'texture']
    },
    keyIngredients: ['salicylic acid (BHA 4%)', 'willow bark', 'niacinamide'],
    ingredientBenefits: 'BHA penetrates pores to dissolve sebum and dead cells. Oil-soluble for deep pore cleansing. Anti-inflammatory properties reduce acne formation.',
    priceRange: '$',
    price: '$15.99',
    tags: ['k-beauty', 'exfoliating', 'acne-control', 'budget'],
    brandWebsite: '',
    affiliateLinks: []
  },
  {
    id: 'axis-y-mugwort-essence',
    brand: 'Axis-Y',
    productCategory: 'Calming Essence Toner',
    recommendedFor: {
      skinTypes: ['sensitive', 'combination', 'oily'],
      acneTypes: ['inflammatory', 'hormonal', 'mild'],
      concerns: ['redness', 'irritation', 'barrier support']
    },
    keyIngredients: ['mugwort extract', 'centella asiatica', 'panthenol'],
    ingredientBenefits: 'Mugwort contains azulene for anti-inflammatory action. Centella promotes wound healing and collagen synthesis. Panthenol strengthens moisture barrier.',
    priceRange: '$',
    price: '$16.50',
    tags: ['k-beauty', 'calming', 'anti-inflammatory', 'gentle', 'budget'],
    brandWebsite: '',
    affiliateLinks: []
  },
  {
    id: 'iunik-centella-gel-cream',
    brand: 'iUNIK',
    productCategory: 'Lightweight Gel Moisturizer',
    recommendedFor: {
      skinTypes: ['oily', 'combination', 'sensitive'],
      acneTypes: ALL_ACNE_TYPES,
      concerns: ['hydration without heaviness', 'soothing']
    },
    keyIngredients: ['centella asiatica (70%)', 'niacinamide', 'adenosine'],
    ingredientBenefits: 'High concentration of centella for wound healing and anti-inflammatory effects. Gel texture absorbs quickly without clogging pores.',
    priceRange: '$',
    price: '$11.99',
    tags: ['k-beauty', 'hydrating', 'calming', 'budget', 'student-pick'],
    brandWebsite: '',
    affiliateLinks: []
  },

  // INFLUENCER CLEAN BEAUTY
  {
    id: 'krave-beauty-kale-lalu',
    brand: 'KraveBeauty',
    productCategory: 'Green Juice Serum',
    recommendedFor: {
      skinTypes: ['oily', 'combination'],
      acneTypes: ['mild', 'hormonal'],
      concerns: ['antioxidant protection', 'sebum control']
    },
    keyIngredients: ['kale extract', 'spinach', 'green tea', 'vitamin C'],
    ingredientBenefits: 'Concentrated vegetable extracts provide antioxidants (vitamins A, C, K). Green tea polyphenols reduce sebum oxidation and inflammation.',
    priceRange: '$$',
    price: '$24.99',
    tags: ['influencer', 'natural', 'anti-inflammatory', 'brightening'],
    brandWebsite: '',
    affiliateLinks: []
  },
  {
    id: 'krave-beauty-beet-shield',
    brand: 'KraveBeauty',
    productCategory: 'Antioxidant Hydration Cream',
    recommendedFor: {
      skinTypes: ['dry', 'combination', 'sensitive'],
      acneTypes: ['post-acne', 'mild'],
      concerns: ['barrier protection', 'environmental defense']
    },
    keyIngredients: ['beetroot extract', 'niacinamide', 'tamanu oil'],
    ingredientBenefits: 'Beetroot betalains provide antioxidant protection. Tamanu oil contains calophyllolide for antibacterial and anti-inflammatory effects.',
    priceRange: '$$',
    price: '$28.00',
    tags: ['influencer', 'natural', 'barrier-repair', 'hydrating'],
    brandWebsite: '',
    affiliateLinks: []
  },
  {
    id: 'glow-recipe-watermelon-toner',
    brand: 'Glow Recipe',
    productCategory: 'Hydrating PHA Toner',
    recommendedFor: {
      skinTypes: ['oily', 'dry', 'combination', 'sensitive', 'normal'],
      acneTypes: ['mild', 'post-acne'],
      concerns: ['gentle exfoliation', 'hydration', 'pore refining']
    },
    keyIngredients: ['watermelon extract', 'PHA', 'hyaluronic acid', 'cactus water'],
    ingredientBenefits: 'PHAs provide gentle exfoliation without irritation. Watermelon contains amino acids and lycopene for hydration and antioxidant protection.',
    priceRange: '$$',
    price: '$32.00',
    tags: ['influencer', 'hydrating', 'gentle', 'exfoliating'],
    brandWebsite: '',
    affiliateLinks: []
  },
  {
    id: 'glow-recipe-niacinamide-dew-drops',
    brand: 'Glow Recipe',
    productCategory: 'Brightening Serum',
    recommendedFor: {
      skinTypes: ['oily', 'combination', 'normal'],
      acneTypes: ['post-acne', 'mild', 'hormonal'],
      concerns: ['dark spots', 'dullness', 'sebum control']
    },
    keyIngredients: ['niacinamide (5%)', 'sea daffodil extract', 'hyaluronic acid'],
    ingredientBenefits: 'Niacinamide inhibits melanosome transfer and regulates sebum. Sea daffodil extract supports cell renewal and brightening.',
    priceRange: '$$',
    price: '$34.00',
    tags: ['influencer', 'brightening', 'hydrating'],
    brandWebsite: '',
    affiliateLinks: []
  },
  {
    id: 'rhode-glazing-fluid',
    brand: 'Rhode',
    productCategory: 'Barrier Support Serum',
    recommendedFor: {
      skinTypes: ['dry', 'combination', 'sensitive'],
      acneTypes: ['post-acne', 'mild'],
      concerns: ['barrier repair', 'hydration', 'glow']
    },
    keyIngredients: ['niacinamide', 'zinc', 'marula oil', 'peptides'],
    ingredientBenefits: 'Zinc supports wound healing and has anti-inflammatory properties. Marula oil provides omega fatty acids for barrier lipid support. Peptides signal collagen production.',
    priceRange: '$$',
    price: '$29.00',
    tags: ['influencer', 'barrier-repair', 'hydrating'],
    brandWebsite: '',
    affiliateLinks: []
  },

  // TARGETED TREATMENTS
  {
    id: 'the-ordinary-niacinamide',
    brand: 'The Ordinary',
    productCategory: 'High-Strength Niacinamide Serum',
    recommendedFor: {
      skinTypes: ['oily', 'combination'],
      acneTypes: ['mild', 'moderate', 'severe', 'hormonal', 'inflammatory', 'post-acne'],
      concerns: ['sebum regulation', 'pore appearance', 'blemishes']
    },
    keyIngredients: ['niacinamide (10%)', 'zinc (1%)'],
    ingredientBenefits: 'High concentration niacinamide reduces sebum production and minimizes pore appearance. Zinc provides additional oil control and anti-inflammatory benefits.',
    priceRange: '$',
    price: '$6.50',
    tags: ['budget', 'acne-control', 'brightening', 'student-pick'],
    brandWebsite: '',
    affiliateLinks: []
  },
  {
    id: 'the-ordinary-azelaic-acid',
    brand: 'The Ordinary',
    productCategory: 'Brightening + Texture Treatment',
    recommendedFor: {
      skinTypes: ['oily', 'combination'],
      acneTypes: ['post-acne', 'mild', 'moderate'],
      concerns: ['hyperpigmentation', 'texture', 'acne marks']
    },
    keyIngredients: ['azelaic acid (10%)'],
    ingredientBenefits: 'Azelaic acid inhibits tyrosinase for hyperpigmentation reduction. Antibacterial against P. acnes. Reduces keratin production to prevent clogged pores.',
    priceRange: '$',
    price: '$8.90',
    tags: ['brightening', 'acne-control', 'budget', 'student-pick'],
    brandWebsite: '',
    affiliateLinks: []
  },
  {
    id: 'cos de-baha-azelaic-acid',
    brand: 'Cos De BAHA',
    productCategory: 'Azelaic Acid Serum',
    recommendedFor: {
      skinTypes: ['oily', 'combination', 'sensitive'],
      acneTypes: ['post-acne', 'hormonal', 'inflammatory'],
      concerns: ['redness', 'dark spots', 'acne prevention']
    },
    keyIngredients: ['azelaic acid (10%)', 'niacinamide', 'tea tree'],
    ingredientBenefits: 'Azelaic acid combined with niacinamide for enhanced brightening. Tea tree oil provides antimicrobial support against acne-causing bacteria.',
    priceRange: '$',
    price: '$12.99',
    tags: ['k-beauty', 'brightening', 'acne-control', 'anti-inflammatory', 'budget'],
    brandWebsite: '',
    affiliateLinks: []
  },
  {
    id: 'purito-centella-serum',
    brand: 'Purito',
    productCategory: 'Barrier Recovery Serum',
    recommendedFor: {
      skinTypes: ['sensitive', 'dry', 'combination'],
      acneTypes: ['inflammatory', 'post-acne'],
      concerns: ['irritation', 'redness', 'compromised barrier']
    },
    keyIngredients: ['centella asiatica (49%)', 'niacinamide', 'peptides'],
    ingredientBenefits: 'Centella extract promotes collagen synthesis and wound healing through asiaticoside and madecassoside. Supports barrier lipid production.',
    priceRange: '$',
    price: '$15.50',
    tags: ['k-beauty', 'calming', 'barrier-repair', 'anti-inflammatory', 'budget'],
    brandWebsite: '',
    affiliateLinks: []
  },
  {
    id: 'isntree-hyaluronic-toner',
    brand: 'Isntree',
    productCategory: 'Multi-Molecular Weight Hydration Toner',
    recommendedFor: {
      skinTypes: ['dry', 'combination', 'sensitive'],
      acneTypes: ALL_ACNE_TYPES,
      concerns: ['dehydration', 'barrier support', 'plumping']
    },
    keyIngredients: ['hyaluronic acid (50%)', 'beta-glucan', 'sodium hyaluronate'],
    ingredientBenefits: 'Multiple molecular weights of hyaluronic acid hydrate at different skin depths. Beta-glucan supports barrier repair and reduces inflammation.',
    priceRange: '$',
    price: '$13.99',
    tags: ['k-beauty', 'hydrating', 'gentle', 'budget', 'student-pick'],
    brandWebsite: '',
    affiliateLinks: []
  },

  // GENTLE EXFOLIATION
  {
    id: 'some-by-mi-aha-bha-pha-toner',
    brand: 'Some By Mi',
    productCategory: 'Triple-Acid Gentle Exfoliant',
    recommendedFor: {
      skinTypes: ['oily', 'combination'],
      acneTypes: ['mild', 'moderate', 'hormonal'],
      concerns: ['texture', 'clogged pores', 'dullness']
    },
    keyIngredients: ['AHA', 'BHA', 'PHA', 'tea tree', 'niacinamide'],
    ingredientBenefits: 'AHA exfoliates surface. BHA penetrates pores. PHA provides gentle exfoliation with humectant properties. Tea tree offers antimicrobial support.',
    priceRange: '$',
    price: '$16.99',
    tags: ['k-beauty', 'exfoliating', 'acne-control', 'budget'],
    brandWebsite: '',
    affiliateLinks: []
  },
  {
    id: 'versed-exfoliating-toner',
    brand: 'Versed',
    productCategory: 'Glycolic Acid Toner',
    recommendedFor: {
      skinTypes: ['oily', 'combination', 'normal'],
      acneTypes: ['mild', 'post-acne'],
      concerns: ['texture', 'dark spots', 'dullness']
    },
    keyIngredients: ['glycolic acid (8.5%)', 'niacinamide', 'aloe'],
    ingredientBenefits: 'Glycolic acid (smallest AHA) penetrates effectively for cell turnover. Combined with soothing agents to minimize irritation.',
    priceRange: '$',
    price: '$14.99',
    tags: ['exfoliating', 'brightening', 'budget', 'student-pick'],
    brandWebsite: '',
    affiliateLinks: []
  },

  // CLEAN MOISTURIZERS
  {
    id: 'elf-holy-hydration',
    brand: 'e.l.f.',
    productCategory: 'Lightweight Gel Moisturizer',
    recommendedFor: {
      skinTypes: ['oily', 'combination'],
      acneTypes: ALL_ACNE_TYPES,
      concerns: ['hydration', 'non-greasy', 'affordable']
    },
    keyIngredients: ['hyaluronic acid', 'niacinamide', 'peptides'],
    ingredientBenefits: 'Gel formula provides hydration without occlusion. Niacinamide for pore refinement. Budget-friendly option for students.',
    priceRange: '$',
    price: '$9.99',
    tags: ['budget', 'hydrating', 'student-pick', 'gentle'],
    brandWebsite: '',
    affiliateLinks: []
  },
  {
    id: 'illiyoon-ceramide-cream',
    brand: 'Illiyoon',
    productCategory: 'Ceramide Barrier Cream',
    recommendedFor: {
      skinTypes: ['dry', 'sensitive', 'combination'],
      acneTypes: ['post-acne', 'inflammatory'],
      concerns: ['damaged barrier', 'dryness', 'sensitivity']
    },
    keyIngredients: ['ceramides', 'shea butter', 'squalane'],
    ingredientBenefits: 'Ceramides replenish lipid barrier. Squalane mimics skin sebum for non-comedogenic occlusion. Supports barrier recovery.',
    priceRange: '$',
    price: '$13.99',
    tags: ['k-beauty', 'barrier-repair', 'hydrating', 'budget'],
    brandWebsite: '',
    affiliateLinks: []
  },

  // SPOT TREATMENTS
  {
    id: 'cosrx-pimple-patch',
    brand: 'Cosrx',
    productCategory: 'Hydrocolloid Acne Patches',
    recommendedFor: {
      skinTypes: ALL_SKIN_TYPES,
      acneTypes: ['mild', 'moderate', 'inflammatory'],
      concerns: ['active breakouts', 'healing', 'protection']
    },
    keyIngredients: ['hydrocolloid'],
    ingredientBenefits: 'Hydrocolloid absorbs excess fluid and maintains moist healing environment. Protects from picking and bacterial contamination.',
    priceRange: '$',
    price: '$5.99',
    tags: ['k-beauty', 'acne-control', 'budget', 'student-pick'],
    brandWebsite: '',
    affiliateLinks: []
  },
  {
    id: 'axis-y-quinoa-eye-serum',
    brand: 'Axis-Y',
    productCategory: 'Brightening Eye Serum',
    recommendedFor: {
      skinTypes: ALL_SKIN_TYPES,
      acneTypes: ['post-acne'],
      concerns: ['dark circles', 'periorbital hyperpigmentation']
    },
    keyIngredients: ['quinoa extract', 'niacinamide', 'caffeine'],
    ingredientBenefits: 'Caffeine constricts blood vessels to reduce puffiness. Niacinamide addresses hyperpigmentation. Quinoa provides peptides and vitamins.',
    priceRange: '$',
    price: '$14.50',
    tags: ['k-beauty', 'brightening', 'budget'],
    brandWebsite: '',
    affiliateLinks: []
  },

  // NATURAL/PLANT-BASED OPTIONS
  {
    id: 'round-lab-birch-toner',
    brand: 'Round Lab',
    productCategory: 'Hydrating Toner',
    recommendedFor: {
      skinTypes: ['dry', 'combination', 'sensitive'],
      acneTypes: ALL_ACNE_TYPES,
      concerns: ['hydration', 'soothing', 'barrier prep']
    },
    keyIngredients: ['birch sap', 'hyaluronic acid', 'panthenol'],
    ingredientBenefits: 'Birch sap contains minerals and amino acids for hydration. Low pH maintains skin barrier integrity. Prepares skin for subsequent products.',
    priceRange: '$',
    price: '$17.99',
    tags: ['k-beauty', 'natural', 'hydrating', 'gentle', 'budget'],
    brandWebsite: '',
    affiliateLinks: []
  },
  {
    id: 'innisfree-green-tea-seed-serum',
    brand: 'Innisfree',
    productCategory: 'Antioxidant Hydration Serum',
    recommendedFor: {
      skinTypes: ['oily', 'combination', 'normal'],
      acneTypes: ['mild', 'hormonal'],
      concerns: ['antioxidant protection', 'sebum balance']
    },
    keyIngredients: ['green tea extract', 'seed oil', 'hyaluronic acid'],
    ingredientBenefits: 'Green tea polyphenols (EGCG) provide antioxidant and anti-inflammatory effects. Regulates sebum production and reduces P. acnes growth.',
    priceRange: '$',
    price: '$18.50',
    tags: ['k-beauty', 'natural', 'hydrating', 'anti-inflammatory', 'budget'],
    brandWebsite: '',
    affiliateLinks: []
  }
];

// Helper functions for recommendations
export const getProductsBySkinType = (skinType: SkinType): Product[] => {
  return products.filter(p => p.recommendedFor.skinTypes.includes(skinType));
};

export const getProductsByAcneType = (acneType: AcneType): Product[] => {
  return products.filter(p => p.recommendedFor.acneTypes.includes(acneType));
};

export const getProductsByTags = (tags: ProductTag[]): Product[] => {
  return products.filter(p => tags.some(tag => p.tags.includes(tag)));
};

export const getProductsByBudget = (maxPrice: PriceRange): Product[] => {
  const priceOrder = { '$': 1, '$$': 2, '$$$': 3 };
  return products.filter(p => priceOrder[p.priceRange] <= priceOrder[maxPrice]);
};

export const getRecommendedProducts = (
  skinType: SkinType,
  acneType: AcneType,
  preferences?: {
    budget?: boolean;
    kbeauty?: boolean;
    influencer?: boolean;
    natural?: boolean;
  }
): Product[] => {
  let filtered = products.filter(p => 
    p.recommendedFor.skinTypes.includes(skinType) &&
    p.recommendedFor.acneTypes.includes(acneType)
  );

  if (preferences?.budget) {
    filtered = filtered.filter(p => p.tags.includes('budget') || p.tags.includes('student-pick'));
  }

  if (preferences?.kbeauty) {
    filtered = filtered.filter(p => p.tags.includes('k-beauty'));
  }

  if (preferences?.influencer) {
    filtered = filtered.filter(p => p.tags.includes('influencer'));
  }

  if (preferences?.natural) {
    filtered = filtered.filter(p => p.tags.includes('natural') || p.tags.includes('gentle'));
  }

  return filtered;
};
